import { useMemo } from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { SupportedCurrency } from '@shared/utils/currency';
import { PRINT_SUPPORTED_CURRENCIES, useSupportedCurrencyOptions } from '@shared/utils/currency';
import { isValidPrintSalesAllowedCountryListPayload } from '@shared/utils/address/printSalesCountryList';

/**
 * Hook that returns an array of supported currencies based on printSalesAllowedCountryList feature flag.
 * Filters for print-enabled countries and maps their currencies to SupportedCurrency objects with names.
 * Returns undefined when feature flag is not enabled or invalid, allowing fallback to default currency list.
 *
 * @returns Array of SupportedCurrency objects with name property, or undefined if feature flag is not enabled/invalid
 */
export const usePrintSupportedCurrencies = (): ReadonlyArray<SupportedCurrency & { name: string }> | undefined => {
  const { value: printSalesAllowedCountryListValue, payload: printSalesAllowedCountryListPayload } = useFeatureValue('printSalesAllowedCountryList');

  const filteredCurrencies = useMemo(() => {
    const isPrintSalesAllowedCountryListPayloadEnabledAndValid =
      printSalesAllowedCountryListValue && isValidPrintSalesAllowedCountryListPayload(printSalesAllowedCountryListPayload);

    if (!isPrintSalesAllowedCountryListPayloadEnabledAndValid) {
      return undefined;
    }

    // Filter for print-enabled countries and extract unique currency codes
    const printCountryList = printSalesAllowedCountryListPayload.filter(item => item.print || item.digital);
    const uniqueCurrencyCodes = new Set(printCountryList.map(item => item.currency));

    const filtered = PRINT_SUPPORTED_CURRENCIES.filter(currency => uniqueCurrencyCodes.has(currency.code));

    return filtered.length > 0 ? filtered : undefined;
  }, [printSalesAllowedCountryListValue, printSalesAllowedCountryListPayload]);

  const currenciesWithNames = useSupportedCurrencyOptions(filteredCurrencies ?? PRINT_SUPPORTED_CURRENCIES);

  return filteredCurrencies === undefined ? undefined : currenciesWithNames;
};
